import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { resolveImageUrl } from '../lib/imageUrl';
import { motion } from 'framer-motion';
import { ArrowRight, Building, Megaphone, Printer, Package, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
  building: Building,
  megaphone: Megaphone,
  printer: Printer,
  package: Package,
};

const heroImage = "https://images.unsplash.com/photo-1631171992385-784ae02b1acb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd29ya2VycyUyMGhlbG1ldCUyMGJsdWVwcmludHxlbnwwfHx8fDE3NzAyOTA3Mzl8MA&ixlib=rb-4.1.0&q=85";

export default function HomePage() {
  const { t, getLocalizedContent } = useLanguage();
  const [services, setServices] = useState([]);
  const [projects, setProjects] = useState([]);
  const [products, setProducts] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, projectsRes, productsRes, partnersRes] = await Promise.all([
        axios.get(`${API}/services`),
        axios.get(`${API}/projects?featured_only=true`),
        axios.get(`${API}/products?featured_only=true`),
        axios.get(`${API}/partners`),
      ]);
      setServices(servicesRes.data);
      setProjects(projectsRes.data.slice(0, 3));
      setProducts(productsRes.data.slice(0, 4));
      setPartners(partnersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#0F172A] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Construction"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/90 to-transparent" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block px-4 py-1 bg-[#0B6AB3]/20 text-[#0B6AB3] text-sm font-medium rounded-full mb-6">
              GTIC SARL - Cameroun
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight tracking-tight font-['Montserrat']">
              {t('hero_title')}
              <br />
              <span className="text-[#0B6AB3]">{t('hero_title_2')}</span>
            </h1>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-xl">
              {t('hero_subtitle')}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/services">
                <Button size="lg" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white px-8" data-testid="hero-cta">
                  {t('hero_cta')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10" data-testid="hero-contact">
                  {t('hero_contact')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur-sm border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0B6AB3]">10+</div>
                <div className="text-sm text-gray-400 mt-1">Années d'expérience</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0B6AB3]">150+</div>
                <div className="text-sm text-gray-400 mt-1">Projets réalisés</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0B6AB3]">50+</div>
                <div className="text-sm text-gray-400 mt-1">Employés</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-[#0B6AB3]">100%</div>
                <div className="text-sm text-gray-400 mt-1">Clients satisfaits</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <span className="text-[#0B6AB3] font-medium uppercase tracking-wider text-sm">
              Ce que nous faisons
            </span>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mt-2 font-['Montserrat']">
              {t('services_title')}
            </h2>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              {t('services_subtitle')}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Building;
              return (
                <motion.div
                  key={service.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="h-full border-0 shadow-sm hover:shadow-lg transition-all duration-300 group bg-white" data-testid={`service-card-${index}`}>
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-[#0B6AB3]/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#0B6AB3] transition-colors">
                        <IconComponent className="h-7 w-7 text-[#0B6AB3] group-hover:text-white transition-colors" />
                      </div>
                      <h3 className="font-semibold text-lg text-[#0F172A] mb-2">
                        {getLocalizedContent(service.title)}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {getLocalizedContent(service.short_description)}
                      </p>
                      <Link
                        to={`/services#${service.id}`}
                        className="inline-flex items-center text-[#0B6AB3] font-medium text-sm hover:gap-2 transition-all"
                      >
                        {t('services_learn_more')}
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Link>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 lg:py-28" data-testid="projects-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <span className="text-[#0B6AB3] font-medium uppercase tracking-wider text-sm">
                Portfolio
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mt-2 font-['Montserrat']">
                {t('projects_title')}
              </h2>
              <p className="text-gray-600 mt-2 max-w-xl">
                {t('projects_subtitle')}
              </p>
            </motion.div>
            <Link to="/projects">
              <Button variant="outline" className="mt-4 md:mt-0 border-[#0B6AB3] text-[#0B6AB3] hover:bg-[#0B6AB3] hover:text-white">
                Voir tous les projets
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: index * 0.15 }}
              >
                <Card className="overflow-hidden border-0 shadow-sm hover:shadow-xl transition-all duration-300 group" data-testid={`project-card-${index}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={resolveImageUrl(project.images?.[0]) || 'https://via.placeholder.com/400x300'}
                      alt={getLocalizedContent(project.title)}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <span className={`absolute top-4 right-4 px-3 py-1 text-xs font-medium rounded-full ${
                      project.category === 'completed' ? 'bg-green-500' : 'bg-[#0B6AB3]'
                    } text-white`}>
                      {project.category === 'completed' ? t('projects_completed') : t('projects_ongoing')}
                    </span>
                  </div>
                  <CardContent className="p-5">
                    <h3 className="font-semibold text-lg text-[#0F172A] mb-2">
                      {getLocalizedContent(project.title)}
                    </h3>
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                      {getLocalizedContent(project.description)}
                    </p>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">{project.location}</span>
                      <span className="text-[#0B6AB3] font-medium">{project.year}</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      {products.length > 0 && (
        <section className="py-20 lg:py-28 bg-[#F8F9FA]" data-testid="products-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
              >
                <span className="text-[#0B6AB3] font-medium uppercase tracking-wider text-sm">
                  Boutique
                </span>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#0F172A] mt-2 font-['Montserrat']">
                  {t('products_title')}
                </h2>
                <p className="text-gray-600 mt-2 max-w-xl">
                  {t('products_subtitle')}
                </p>
              </motion.div>
              <Link to="/shop">
                <Button variant="outline" className="mt-4 md:mt-0 border-[#0B6AB3] text-[#0B6AB3] hover:bg-[#0B6AB3] hover:text-white">
                  Voir la boutique
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {products.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="overflow-hidden border-0 shadow-sm hover:shadow-lg transition-all duration-300 bg-white" data-testid={`product-card-${index}`}>
                    <div className="relative h-48 overflow-hidden bg-gray-100">
                      <img
                        src={resolveImageUrl(product.images?.[0]) || 'https://via.placeholder.com/300x200'}
                        alt={getLocalizedContent(product.name)}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-[#0F172A] mb-1 line-clamp-1">
                        {getLocalizedContent(product.name)}
                      </h3>
                      <p className="text-[#0B6AB3] font-bold">
                        {product.price?.toLocaleString()} {t('common_fcfa')}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Partners Section */}
      {partners.length > 0 && (
        <section className="py-16 lg:py-20" data-testid="partners-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="text-center mb-10"
            >
              <h2 className="text-2xl lg:text-3xl font-bold text-[#0F172A] font-['Montserrat']">
                {t('partners_title')}
              </h2>
              <p className="text-gray-600 mt-2">
                {t('partners_subtitle')}
              </p>
            </motion.div>

            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
              {partners.map((partner, index) => (
                <motion.div
                  key={partner.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                  className="grayscale hover:grayscale-0 transition-all duration-300"
                  data-testid={`partner-${index}`}
                >
                  <img
                    src={partner.logo_url || 'https://via.placeholder.com/120x60'}
                    alt={partner.name}
                    className="h-12 w-auto object-contain"
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#0B6AB3]" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-white font-['Montserrat']">
              Prêt à démarrer votre projet ?
            </h2>
            <p className="text-white/80 mt-4 text-lg max-w-2xl mx-auto">
              Contactez-nous dès aujourd'hui pour discuter de vos besoins et obtenir un devis personnalisé.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[#0B6AB3] hover:bg-gray-100 px-8" data-testid="cta-contact">
                  Nous contacter
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="tel:+237686895817">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                  +237 6 86 89 58 17
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
